import React, { useState, useEffect, useCallback } from 'react';
import { portfolioLabAPI, trackActivity } from '../../services/api';
import FundSearch from './components/FundSearch';
import PortfolioBuilder from './components/PortfolioBuilder';
import PortfolioBlend from './components/PortfolioBlend';
import SavedPortfolios from './components/SavedPortfolios';
import PortfolioComparison from './components/PortfolioComparison';
import BulkUpload from './components/BulkUpload';
import './PortfolioLabApp.css';

const PortfolioLabApp = () => {
  const [activeTab, setActiveTab] = useState('build');
  // Build sub-mode: 'funds' = fund-by-fund builder, 'blend' = combine two saved portfolios
  const [buildMode, setBuildMode] = useState('funds');

  // Portfolio state
  const [selectedFunds, setSelectedFunds] = useState([]);
  const [currentPortfolioId, setCurrentPortfolioId] = useState(null);
  const [currentPortfolioName, setCurrentPortfolioName] = useState('');
  const [currentClient, setCurrentClient] = useState('');
  const [currentPortfolioCode, setCurrentPortfolioCode] = useState('');
  const [currentDescription, setCurrentDescription] = useState('');
  // Return objective (return_objective stored as a decimal, e.g. 0.035)
  const [currentObjectiveType, setCurrentObjectiveType] = useState('cpi');
  const [currentReturnObjective, setCurrentReturnObjective] = useState(null);
  const [currentTimeHorizon, setCurrentTimeHorizon] = useState(null);
  const [savedPortfolios, setSavedPortfolios] = useState([]);
  const [loadingPortfolios, setLoadingPortfolios] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  // Gearing: when enabled, negative (borrowed/short) weights are allowed as
  // long as the book still NETS to 100%. Auto-enabled when loading a
  // portfolio that already holds negative weights.
  const [gearingEnabled, setGearingEnabled] = useState(false);

  useEffect(() => {
    trackActivity('app_access', 'portfolio_lab');
    loadSavedPortfolios();
  }, []);

  const loadSavedPortfolios = async () => {
    setLoadingPortfolios(true);
    try {
      const data = await portfolioLabAPI.listPortfolios();
      setSavedPortfolios(data);
    } catch (err) {
      console.error('Failed to load portfolios:', err);
    } finally {
      setLoadingPortfolios(false);
    }
  };

  const handleAddFund = useCallback((fund) => {
    setSelectedFunds(prev => {
      if (prev.some(f => f.fund_code === fund.tr_code)) return prev;
      return [...prev, {
        fund_code: fund.tr_code,
        fund_name: fund.fund_name,
        weight: 0,
        asset_class: fund.asset_class_group || fund.asset_class,
        fee: fund.fee,
      }];
    });
    setIsDirty(true);
  }, []);

  const handleUpdateWeight = useCallback((fundCode, weight) => {
    setSelectedFunds(prev =>
      prev.map(f => f.fund_code === fundCode ? { ...f, weight } : f)
    );
    setIsDirty(true);
  }, []);

  const handleRemoveFund = useCallback((fundCode) => {
    setSelectedFunds(prev => prev.filter(f => f.fund_code !== fundCode));
    setIsDirty(true);
  }, []);

  const handleSave = async ({ name, client, portfolioCode, description, objectiveType, returnObjective, timeHorizon }) => {
    const holdings = selectedFunds.map(f => ({
      fund_code: f.fund_code,
      fund_name: f.fund_name,
      weight: f.weight,
    }));

    try {
      let result;
      const payload = {
        name,
        client: client || null,
        portfolio_code: portfolioCode || null,
        description,
        objective_type: objectiveType || null,
        return_objective: returnObjective ?? null,
        time_horizon: timeHorizon ?? null,
        holdings,
      };
      if (currentPortfolioId) {
        result = await portfolioLabAPI.updatePortfolio(currentPortfolioId, payload);
      } else {
        result = await portfolioLabAPI.createPortfolio(payload);
      }
      setCurrentPortfolioId(result.id);
      setCurrentPortfolioName(result.name);
      setCurrentClient(client || '');
      setCurrentPortfolioCode(portfolioCode || '');
      setCurrentDescription(description || '');
      setCurrentObjectiveType(objectiveType || 'cpi');
      setCurrentReturnObjective(returnObjective ?? null);
      setCurrentTimeHorizon(timeHorizon ?? null);
      setIsDirty(false);
      await loadSavedPortfolios();
      return result;
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to save portfolio';
      throw new Error(detail);
    }
  };

  const handleSaveAs = async ({ name, client, portfolioCode, description, objectiveType, returnObjective, timeHorizon }) => {
    const holdings = selectedFunds.map(f => ({
      fund_code: f.fund_code,
      fund_name: f.fund_name,
      weight: f.weight,
    }));

    try {
      const payload = {
        name,
        client: client || null,
        portfolio_code: portfolioCode || null,
        description,
        objective_type: objectiveType || null,
        return_objective: returnObjective ?? null,
        time_horizon: timeHorizon ?? null,
        holdings,
      };
      const result = await portfolioLabAPI.createPortfolio(payload);
      setCurrentPortfolioId(result.id);
      setCurrentPortfolioName(result.name);
      setCurrentClient(client || '');
      setCurrentPortfolioCode(portfolioCode || '');
      setCurrentDescription(description || '');
      setCurrentObjectiveType(objectiveType || 'cpi');
      setCurrentReturnObjective(returnObjective ?? null);
      setCurrentTimeHorizon(timeHorizon ?? null);
      setIsDirty(false);
      await loadSavedPortfolios();
      return result;
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to save portfolio';
      throw new Error(detail);
    }
  };

  const handleEditMeta = async (id, { name, client, portfolio_code, description, objective_type, return_objective, time_horizon }) => {
    try {
      await portfolioLabAPI.updatePortfolio(id, {
        name,
        client: client || null,
        portfolio_code: portfolio_code || null,
        description: description || null,
        objective_type: objective_type || null,
        return_objective: return_objective ?? null,
        time_horizon: time_horizon ?? null,
      });
      // If we're editing the currently loaded portfolio, update local state
      if (currentPortfolioId === id) {
        setCurrentPortfolioName(name);
        setCurrentClient(client || '');
        setCurrentPortfolioCode(portfolio_code || '');
        setCurrentDescription(description || '');
        setCurrentObjectiveType(objective_type || 'cpi');
        setCurrentReturnObjective(return_objective ?? null);
        setCurrentTimeHorizon(time_horizon ?? null);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to update portfolio';
      throw new Error(detail);
    }
  };

  // Saved Portfolios is an editable grid: several rows can be dirty at once, so
  // they are written in one go and the list refreshed once at the end.
  const handleSaveMeta = async (updates) => {
    for (const u of updates) {
      await handleEditMeta(u.id, u.fields);
    }
    await loadSavedPortfolios();
  };

  const handleLoadPortfolio = async (portfolio) => {
    try {
      const data = await portfolioLabAPI.getPortfolio(portfolio.id);

      // Look up asset class groups for all fund codes
      const trCodes = data.holdings.map(h => h.fund_code).filter(Boolean);
      let assetClassLookup = {};
      if (trCodes.length > 0) {
        try {
          const lookupRes = await portfolioLabAPI.assetClassLookup(trCodes);
          assetClassLookup = lookupRes.lookup || {};
        } catch (e) {
          console.error('Asset class lookup failed:', e);
        }
      }

      setSelectedFunds(data.holdings.map(h => ({
        fund_code: h.fund_code,
        fund_name: h.fund_name,
        weight: h.weight,
        asset_class: assetClassLookup[h.fund_code] || null,
        fee: null,
      })));
      setGearingEnabled(data.holdings.some(h => h.weight < 0));
      setCurrentPortfolioId(data.id);
      setCurrentPortfolioName(data.name);
      setCurrentClient(data.client || '');
      setCurrentPortfolioCode(data.portfolio_code || '');
      setCurrentDescription(data.description || '');
      setCurrentObjectiveType(data.objective_type || 'cpi');
      setCurrentReturnObjective(data.return_objective ?? null);
      setCurrentTimeHorizon(data.time_horizon ?? null);
      setIsDirty(false);
      setActiveTab('build');
    } catch (err) {
      console.error('Failed to load portfolio:', err);
    }
  };

  const handleDeletePortfolio = async (id) => {
    try {
      await portfolioLabAPI.deletePortfolio(id);
      if (currentPortfolioId === id) {
        setCurrentPortfolioId(null);
        setCurrentPortfolioName('');
        setCurrentClient('');
        setCurrentPortfolioCode('');
        setCurrentDescription('');
        setCurrentObjectiveType('cpi');
        setCurrentReturnObjective(null);
        setCurrentTimeHorizon(null);
        setSelectedFunds([]);
        setIsDirty(false);
      }
      await loadSavedPortfolios();
    } catch (err) {
      console.error('Failed to delete portfolio:', err);
    }
  };

  const handleDuplicatePortfolio = async (id) => {
    try {
      await portfolioLabAPI.duplicatePortfolio(id);
      await loadSavedPortfolios();
    } catch (err) {
      console.error('Failed to duplicate portfolio:', err);
    }
  };

  const handleNewPortfolio = () => {
    setSelectedFunds([]);
    setCurrentPortfolioId(null);
    setCurrentPortfolioName('');
    setCurrentClient('');
    setCurrentPortfolioCode('');
    setCurrentDescription('');
    setCurrentObjectiveType('cpi');
    setCurrentReturnObjective(null);
    setCurrentTimeHorizon(null);
    setIsDirty(false);
    setGearingEnabled(false);
    setActiveTab('build');
  };

  // Blended result from two saved portfolios → load into the builder as a new, unsaved
  // portfolio so it can be tweaked and saved like any other.
  const handleLoadBlend = (mergedFunds) => {
    setSelectedFunds(mergedFunds.map(f => ({
      fund_code: f.fund_code,
      fund_name: f.fund_name,
      weight: f.weight,
      asset_class: f.asset_class || null,
      fee: null,
    })));
    setCurrentPortfolioId(null);
    setCurrentPortfolioName('');
    setCurrentClient('');
    setCurrentPortfolioCode('');
    setCurrentDescription('');
    setCurrentObjectiveType('cpi');
    setCurrentReturnObjective(null);
    setCurrentTimeHorizon(null);
    setGearingEnabled(mergedFunds.some(f => (f.weight || 0) < 0));
    setIsDirty(true);
    setBuildMode('funds');
  };

  const selectedFundCodes = selectedFunds.map(f => f.fund_code);
  const isGeared = selectedFunds.some(f => (f.weight || 0) < 0);

  return (
    <div className="portfolio-lab">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h4 className="mb-0" style={{ color: 'var(--primary-color)', fontWeight: 700 }}>
          Portfolio Lab
        </h4>
        <div className="d-flex align-items-center gap-2">
          {isGeared && activeTab === 'build' && (
            <span className="badge geared-badge" title="This portfolio uses gearing — it holds negative (borrowed/short) weights and nets to 100%">
              ⚠ GEARED
            </span>
          )}
          {currentPortfolioName && activeTab === 'build' && (
            <span className="badge" style={{ background: 'var(--primary-color)', fontSize: '0.82rem', padding: '0.4rem 0.75rem' }}>
              {currentPortfolioName} {isDirty && '(unsaved changes)'}
            </span>
          )}
        </div>
      </div>

      <div className="lab-shell">
        <aside className="lab-sidebar">
          <button
            className={`lab-nav-item ${activeTab === 'build' ? 'active' : ''}`}
            onClick={() => setActiveTab('build')}
          >
            <i className="bi bi-tools" />
            <span>Build</span>
          </button>
          <button
            className={`lab-nav-item ${activeTab === 'saved' ? 'active' : ''}`}
            onClick={() => setActiveTab('saved')}
          >
            <i className="bi bi-bookmark" />
            <span>Saved Portfolios</span>
          </button>
          <button
            className={`lab-nav-item ${activeTab === 'compare' ? 'active' : ''}`}
            onClick={() => setActiveTab('compare')}
          >
            <i className="bi bi-bar-chart" />
            <span>Compare</span>
          </button>
        </aside>

        <main className="lab-main">
          {activeTab === 'build' && (
            <>
            <div className="build-subtabs">
              <button
                className={`build-subtab ${buildMode === 'funds' ? 'active' : ''}`}
                onClick={() => setBuildMode('funds')}
              >
                From Funds
              </button>
              <button
                className={`build-subtab ${buildMode === 'blend' ? 'active' : ''}`}
                onClick={() => setBuildMode('blend')}
              >
                From Portfolios
              </button>
              <div className="build-subtabs-right">
                <BulkUpload onImported={() => loadSavedPortfolios()} />
              </div>
            </div>
            {buildMode === 'blend' ? (
              <PortfolioBlend savedPortfolios={savedPortfolios} onLoadBlend={handleLoadBlend} />
            ) : (
            <div className="build-layout">
              <div className="panel fund-search">
                <div className="panel-header">
                  <h5>Fund Search</h5>
                </div>
                <FundSearch
                  onAddFund={handleAddFund}
                  selectedFundCodes={selectedFundCodes}
                />
              </div>
              <div className="panel portfolio-builder">
                <div className="panel-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h5 style={{ margin: 0 }}>Portfolio Holdings</h5>
                  {selectedFunds.length > 0 && (
                    <button
                      className="btn btn-sm btn-outline-danger"
                      style={{ fontSize: '0.78rem' }}
                      onClick={() => {
                        if (window.confirm('Clear all funds and start a new portfolio?')) handleNewPortfolio();
                      }}
                    >
                      New Portfolio
                    </button>
                  )}
                </div>
                <PortfolioBuilder
                  funds={selectedFunds}
                  onUpdateWeight={handleUpdateWeight}
                  onRemoveFund={handleRemoveFund}
                  onSave={handleSave}
                  onSaveAs={handleSaveAs}
                  onClear={handleNewPortfolio}
                  currentPortfolioId={currentPortfolioId}
                  currentPortfolioName={currentPortfolioName}
                  currentClient={currentClient}
                  currentPortfolioCode={currentPortfolioCode}
                  currentDescription={currentDescription}
                  currentObjectiveType={currentObjectiveType}
                  currentReturnObjective={currentReturnObjective}
                  currentTimeHorizon={currentTimeHorizon}
                  isDirty={isDirty}
                  gearingEnabled={gearingEnabled}
                  onToggleGearing={setGearingEnabled}
                />
              </div>
            </div>
            )}
            </>
          )}

          {activeTab === 'saved' && (
            <SavedPortfolios
              portfolios={savedPortfolios}
              loading={loadingPortfolios}
              activeId={currentPortfolioId}
              onLoad={handleLoadPortfolio}
              onDelete={handleDeletePortfolio}
              onDuplicate={handleDuplicatePortfolio}
              onNew={handleNewPortfolio}
              onSaveMeta={handleSaveMeta}
            />
          )}

          {activeTab === 'compare' && (
            <PortfolioComparison
              savedPortfolios={savedPortfolios}
              onRefreshPortfolios={loadSavedPortfolios}
            />
          )}
        </main>
      </div>
    </div>
  );
};

export default PortfolioLabApp;
